import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { WhatsappLogo, PaperPlaneTilt, Clock, EnvelopeSimple } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { api, waLink, WHATSAPP_NUMBER, CONTACT_EMAIL, formatApiErrorDetail } from "@/lib/api";

const prettyPhone = `+${WHATSAPP_NUMBER.slice(0, 2)} ${WHATSAPP_NUMBER.slice(2, 5)} ${WHATSAPP_NUMBER.slice(5, 7)} ${WHATSAPP_NUMBER.slice(7)}`;

export const ContactSection = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 md:py-36 bg-sand">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-12 md:gap-16">
        <div className="col-span-12 md:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-terracotta mb-4">{t("contact.kicker")}</p>
            <h2 className="font-serif text-5xl md:text-6xl text-ink leading-[1.02]">{t("contact.title")}</h2>
            <p className="mt-6 text-ink/55 font-light leading-relaxed max-w-sm">{t("contact.sub")}</p>

            <div className="mt-12 flex flex-col gap-6">
              <a
                data-testid="contact-whatsapp-link"
                href={waLink(t("whatsapp.generic"))}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 rounded-full bg-[#25D366]/10 text-[#1da851] flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white transition-colors duration-300">
                  <WhatsappLogo size={20} weight="fill" />
                </span>
                <span className="text-sm tracking-wide text-ink group-hover:text-terracotta transition-colors">{prettyPhone}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-atlantic/10 text-atlantic flex items-center justify-center">
                  <Clock size={20} weight="light" />
                </span>
                <span className="text-sm tracking-wide text-ink/70">{t("contact.response")}</span>
              </div>
              {CONTACT_EMAIL && (
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex items-center gap-4 group"
                >
                  <span className="w-11 h-11 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center group-hover:bg-terracotta group-hover:text-white transition-colors duration-300">
                    <EnvelopeSimple size={20} />
                  </span>
                  <span className="text-sm tracking-wide text-ink group-hover:text-terracotta transition-colors">{CONTACT_EMAIL}</span>
                </a>
              )}
            </div>
          </Reveal>
        </div>

        <div className="col-span-12 md:col-span-7 md:pl-10 md:border-l border-line">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input data-testid="contact-name" required className="input-line" placeholder={t("contact.name")} value={form.name} onChange={set("name")} />
                <input data-testid="contact-email" required type="email" className="input-line" placeholder={t("contact.email")} value={form.email} onChange={set("email")} />
              </div>
              <input data-testid="contact-phone" className="input-line" placeholder={t("contact.phone")} value={form.phone} onChange={set("phone")} />
              <textarea data-testid="contact-message" required rows={4} className="input-line resize-none" placeholder={t("contact.message")} value={form.message} onChange={set("message")} />

              <div className="flex flex-wrap gap-4 mt-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="flex items-center gap-3 px-8 py-4 bg-atlantic text-sand text-xs uppercase tracking-[0.2em] hover:bg-terracotta transition-colors duration-300 disabled:opacity-50"
                >
                  <PaperPlaneTilt size={16} />
                  {sending ? "…" : t("contact.send")}
                </button>
                <a
                  data-testid="contact-whatsapp-btn"
                  href={waLink(form.message ? `${form.name ? `Hola, soy ${form.name}. ` : "Hola. "}${form.message}` : t("whatsapp.generic"))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-8 py-4 border border-ink/25 text-ink text-xs uppercase tracking-[0.2em] hover:border-[#25D366] hover:text-[#1da851] transition-colors duration-300"
                >
                  <WhatsappLogo size={16} />
                  {t("contact.whatsapp")}
                </a>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
